import { CLIENTS, GET_CLIENT_STATUS_TYPE } from "../../../../utils/data/models";
import * as types from "../../../../utils/data/dataActionTypes";
import * as clientTypes from "./clientActionTypes";
import { SubmissionError } from "redux-form";
import ClientApi from "./clientApi";
import { AUTOMATIC } from "../enums/automaticMemberIdentifierOptions";
import { toast } from "react-toastify";
import { FormattedMessage } from "react-intl";
import React from "react/lib/React";
import simpleFetch from "../../../../../utils/simpleFetch";

export function setLastGeneratedMember(data) {
  return {
    type: clientTypes.SET_LAST_GENERATED_MEMBER,
    payload: ClientApi.setLastGeneratedMember(data),
  };
}

export function setClientSelectorClient(client) {
  return {
    type: clientTypes.SET_CLIENT_SELECTOR_CLIENT,
    payload: ClientApi.setClientSelectorClient(client)
  };
}

export function setMintEnabledStatus(isMintEnabled) {
  // Action to store MINT enabled status in Redux at the client level
  return {
    type: clientTypes.SET_MINT_ENABLED_STATUS,
    payload: isMintEnabled,
  };
}

export function setMenrolEnabledStatus(isMenrolEnabled) {
  return {
    type: clientTypes.SET_MENROL_ENABLED_STATUS,
    payload: isMenrolEnabled,
  };
}

/**
 * Patches only the clientStatusType on the active client in Redux.
 * Used to keep the header status fresh on tab navigation without a full re-fetch.
 */
export function patchClientStatusType(clientStatusType) {
  return {
    type: clientTypes.PATCH_CLIENT_STATUS_TYPE,
    payload: clientStatusType,
  };
}

/**
 * Thunk that fetches the latest clientStatusType and patches Redux + localStorage.
 * Used on route navigation and in-component tab switches to avoid stale status display.
 */
export function refreshClientStatusType(clientId) {
  return (dispatch) => {
    const endpoint = `${GET_CLIENT_STATUS_TYPE.path.replace("{}", clientId)}?t=${Date.now()}`;
    return simpleFetch("GET", endpoint, {})
      .then((res) => {
        if (res && res.clientStatusType) {
          localStorage.setItem("clientStatusType", res.clientStatusType);
          dispatch(patchClientStatusType(res.clientStatusType));
        }
      })
      .catch(() => {
        toast(
          <FormattedMessage id="validation.customMessages.serverErrors.genericError" />,
          { type: toast.TYPE.ERROR, autoClose: false }
        );
      });
  };
}

export function updateClientAndValidate(
  dataActions,
  values,
  callBack,
  handleServerError,
  actionType,
  showSingleModalError,
  userLanguage
) {
  /**
   * Function that is used by ClientPage.js and ClientsDataTable.js.
   * Checks for errors returned from the api, if there are no errors, the
   * client data is refreshed on the page.
   */
  let serverValidationErrors = {};

  const selectInputValue = values["isAutomaticMemberIdentifier"];
  values["isAutomaticMemberIdentifier"] = selectInputValue === AUTOMATIC.value;

  delete values.isBilledPreviousPeriod;
  delete values.clientPartnerHash;

  return dataActions.updateDataItem(CLIENTS, values).then((data) => {
    if (data.type === types.UPDATE_DATA_ITEM_FAILURE) {
      // Ready to send multiple errors back to the form if/when the backend returns multiple errors at a time
      if (data.payload.error.data.error === "missing_fields_error") {
        let serverMessage = data.payload.error.data.message
          .split(":")[1]
          .trim();

        handleServerError(serverMessage, actionType, userLanguage);
      }

      if (data.payload.error.data.error === "modify_archived_data") {
        toast(<FormattedMessage id="validation.archiveForbidden" />,
          {
            type: toast.TYPE.ERROR,
            autoClose: false,
          }
        );
      }

      if (
        data.payload.error.data.error === "missing_fields_for_activation_error"
      ) {
        let serverMessage = data.payload.error.data.message
          .split(":")[1]
          .trim();
        handleServerError(
          serverMessage,
          undefined,
          userLanguage,
          "validation.clientFieldValidation.clientChangeStatusValidationError"
        );
      }
      if (data.payload.error.data.error === "duplicateDesignator") {
        serverValidationErrors.designator = {
          messageKey:
            "validation.customMessages.serverErrors.duplicateDesignator",
          values: {},
          state: "error",
        };
      }
      if (data.payload.error.data.error === "existing_client_bad_member_configuration") {
        serverValidationErrors.clientNumber = {
          messageKey: "validation.customMessages.serverErrors.existingClientConfigurationError",
          values: {},
          state: "error"
        };
      }
      if (data.payload.error.data.error === "duplicateClientName") {
        serverValidationErrors.name = {
          messageKey:
            "validation.customMessages.serverErrors.duplicateClientName",
          values: {},
          state: "error",
        };
      }
      if (data.payload.error.data.error === "dateRequired") {
        serverValidationErrors.coveragePeriod = {
          startDate: {
            messageKey: "validation.customMessages.serverErrors.dateRequired",
            values: {},
            state: "error",
          },
          endDate: {
            messageKey: "validation.customMessages.serverErrors.dateRequired",
            values: {},
            state: "error",
          },
        };
      }
      if (
        data.payload.error.data.error === "client_number_error"
      ) {
        serverValidationErrors.clientNumber = {
          messageKey:
            "validation.clientFieldValidation.serverErrors.clientNumberError",
          values: {},
          state: "error",
        };
      }
      if (data.payload.error.data.error === "businessAccountTypeUneditable") {
        serverValidationErrors.businessAccountType = {
          messageKey:
            "validation.customMessages.serverErrors.businessAccountTypeUneditable",
          values: {},
          state: "error",
        };
      }
      if (data.payload.error.data.error === "client_number_mandatory_error") {
        /**
         * Handle error when passing to MEV -> Active. A client number is mandatory in this case.
         */
        let errorCode = data.payload.error.data.error;
        showSingleModalError(errorCode, userLanguage, actionType);
        serverValidationErrors.clientNumber = {
          messageKey:
            "validation.clientFieldValidation.clientNumberMandatoryWarn",
          values: {},
          state: "error",
        };
      }

      if (data.payload.error.data.error === "client_number_uneditable_error ") {
        serverValidationErrors.clientNumber = {
          messageKey:
            "validation.customMessages.serverErrors.clientNumberUneditable",
          values: {},
          state: "error",
        };
      }

      if (
        data.payload.error.data.error ===
        "memberIdentifierConfigurationUneditable"
      ) {
        serverValidationErrors.memberIdentifierType = {
          messageKey:
            "validation.customMessages.serverErrors.memberIdentifierConfigurationUneditable",
          values: {},
          state: "error",
        };
      }

      if (data.payload.error.data.error === "invalid_client_bloc_error") {
        serverValidationErrors.blocId = {
          messageKey:
            "validation.customMessages.serverErrors.invalidClientBlocError",
          values: {},
          state: "error",
        };
      }

      if (data.payload.error.data.error === "manualMemberIdentifierType") {
        serverValidationErrors.clientNumber = {
          messageKey:
            "validation.customMessages.serverErrors.manualMemberIdentifierType",
          values: {},
          state: "error",
        };
      }

      if (serverValidationErrors) {
        throw new SubmissionError(serverValidationErrors);
      }
    } else {
      callBack();
    }
  });
}



// WEBPACK FOOTER //
// ./src/components/dashboard/clients/client/clientActions.js